import React, { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import DateRangePicker from "../../../components/ui/DateRangePicker";
import DataTable from "../../../components/ui/DataTable";
import Button from "../../../components/ui/Button";
import Badge from "../../../components/ui/Badge";
import PlanBadge from "../../../components/ui/PlanBadge";
import Pagination from "../../../components/ui/Pagination";
import { REPORT_OPTIONS } from "../constants/reportOptions";
import { adminService } from "../services/adminService";

const PAGE_SIZE = 25;
const PLAN_MIX_PAGE_SIZE = 25;
const ASSET_INVENTORY_PAGE_SIZE = 25;
const ANALYSIS_PIPELINE_PAGE_SIZE = 25;
const POWER_USERS_PAGE_SIZE = 25;
const PLAN_VS_USAGE_PAGE_SIZE = 25;
const REPORT_GEN_VOLUME_PAGE_SIZE = 25;
const STALE_ASSETS_PAGE_SIZE = 25;
const STALE_JOBS_PAGE_SIZE = 25;
const ENTERPRISE_FUNNEL_PAGE_SIZE = 25;
const ACTIVITY_AUDIT_PAGE_SIZE = 25;

const toTitle = (value = "") =>
  value
    .toString()
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const formatDateTime = (isoString) => {
  if (!isoString) return "—";

  return new Date(isoString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCleanDate = (isoString) => {
  if (!isoString) return "—";

  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getInitials = (name = "") => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "NA";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const getRoleBadgeClass = (role = "") =>
  role.toLowerCase() === "admin"
    ? "bg-[#EAF0FF] text-[#3556C9]"
    : "bg-[#EEF4FF] text-[#4362D4]";

const getPlanBadgeClass = (plan = "") => {
  const value = plan.toLowerCase();

  if (value === "professional") return "bg-[#F1EEFF] text-[#5B4FCF]";
  if (value === "business") return "bg-[#FFF2E8] text-[#C96A2D]";
  if (value === "individual") return "bg-[#F3F4F6] text-[#5F6673]";

  return "bg-[#EEF1F7] text-[#677185]";
};

const STATUS_COLOR = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-gray-100 text-gray-500",
  canceled: "bg-red-100 text-red-600",
};

const ASSET_STATUS_BADGE_VARIANT = {
  complete: "success",
  processing: "warning",
  failed: "danger",
};

const ANALYSIS_STATUS_BADGE_VARIANT = {
  completed: "success",
  running: "warning",
  failed: "danger",
  queued: "muted",
};

const STALE_ASSETS_COLUMNS = [
  {
    header: "Asset ID",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.assetId}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Asset title",
    render: (row) => (
      <span className="text-sm text-brand-text block max-w-[180px] truncate" title={row.assetTitle}>
        {row.assetTitle || "—"}
      </span>
    ),
  },
  {
    header: "Owner name",
    render: (row) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#ECE9FF] text-[#5B4FCF] text-xs font-semibold flex items-center justify-center shrink-0">
          {getInitials(row.ownerName)}
        </div>
        <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.ownerName}</span>
      </div>
    ),
  },
  {
    header: "Owner email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[220px] truncate" title={row.ownerEmail}>
        {row.ownerEmail}
      </span>
    ),
  },
  {
    header: "Status",
    render: (row) => (
      <Badge variant={ASSET_STATUS_BADGE_VARIANT[row.status?.toLowerCase()] || "muted"}>
        {toTitle(row.status || "")}
      </Badge>
    ),
  },
  {
    header: "Last updated",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.lastUpdatedDate)}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Stale duration",
    render: (row) => (
      <span className="text-sm font-medium text-red-500 whitespace-nowrap">{row.staleDuration || "—"}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
];

const ACTIVITY_AUDIT_COLUMNS = [
  {
    header: "Activity type",
    render: (row) => (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#ECE9FF] text-[#5B4FCF] whitespace-nowrap">
        {row.activityType || "\u2014"}
      </span>
    ),
  },
  {
    header: "User name",
    render: (row) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#ECE9FF] text-[#5B4FCF] text-xs font-semibold flex items-center justify-center shrink-0">
          {getInitials(row.userName)}
        </div>
        <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.userName}</span>
      </div>
    ),
  },
  {
    header: "User email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[220px] truncate" title={row.userEmail}>
        {row.userEmail}
      </span>
    ),
  },
  {
    header: "Entity type",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{row.entityType || "\u2014"}</span>
    ),
  },
  {
    header: "Status",
    render: (row) => {
      const s = (row.activityStatus || "").toLowerCase();
      const cls =
        s === "completed" || s === "complete" || s === "done"
          ? "bg-emerald-100 text-emerald-700"
          : s === "failed" || s === "error"
          ? "bg-red-100 text-red-600"
          : "bg-gray-100 text-gray-500";
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls} whitespace-nowrap`}>
          {toTitle(row.activityStatus || "\u2014")}
        </span>
      );
    },
  },
  {
    header: "Timestamp",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.activityTimestamp)}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
];

const ENTERPRISE_FUNNEL_COLUMNS = [
  {
    header: "Inquiry ID",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.inquiryId}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Name",
    render: (row) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#ECE9FF] text-[#5B4FCF] text-xs font-semibold flex items-center justify-center shrink-0">
          {getInitials(row.contactName)}
        </div>
        <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.contactName}</span>
      </div>
    ),
  },
  {
    header: "Email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[220px] truncate" title={row.contactEmail}>
        {row.contactEmail}
      </span>
    ),
  },
  {
    header: "Company",
    render: (row) => (
      <span className="text-sm text-brand-text block max-w-[180px] truncate" title={row.companyName}>
        {row.companyName || "—"}
      </span>
    ),
  },
  {
    header: "Status",
    render: (row) => {
      const status = (row.status || "").toLowerCase();
      const cls =
        status === "contacted"     ? "bg-emerald-100 text-emerald-700" :
        status === "pending"       ? "bg-yellow-100 text-yellow-700" :
        status === "not_contacted" ? "bg-gray-100 text-gray-500" :
                                     "bg-gray-100 text-gray-500";
      const label =
        status === "not_contacted" ? "Not contacted" :
        status.charAt(0).toUpperCase() + status.slice(1);
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
          {label || "—"}
        </span>
      );
    },
  },
  {
    header: "Assigned owner",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{row.assignedOwner || "—"}</span>
    ),
  },
  {
    header: "Created at",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.createdDate)}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
];

const STALE_JOBS_COLUMNS = [
  {
    header: "Job ID",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.jobId}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Job name",
    render: (row) => (
      <span className="text-sm text-brand-text block max-w-[180px] truncate" title={row.jobName}>
        {row.jobName || "—"}
      </span>
    ),
  },
  {
    header: "Owner name",
    render: (row) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#ECE9FF] text-[#5B4FCF] text-xs font-semibold flex items-center justify-center shrink-0">
          {getInitials(row.ownerName)}
        </div>
        <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.ownerName}</span>
      </div>
    ),
  },
  {
    header: "Owner email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[220px] truncate" title={row.ownerEmail}>
        {row.ownerEmail}
      </span>
    ),
  },
  {
    header: "Status",
    render: (row) => (
      <Badge variant={ANALYSIS_STATUS_BADGE_VARIANT[row.status?.toLowerCase()] || "muted"}>
        {toTitle(row.status || "")}
      </Badge>
    ),
  },
  {
    header: "Created at",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.createdDate)}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Stale duration",
    render: (row) => (
      <span className="text-sm font-medium text-red-500 whitespace-nowrap">{row.staleDuration || "—"}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
];

const PLAN_MIX_COLUMNS = [
  {
    header: "Name",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.name}</span>
    ),
  },
  {
    header: "Email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[240px] truncate" title={row.email}>
        {row.email}
      </span>
    ),
  },
  {
    header: "Plan tier",
    render: (row) => <PlanBadge plan={row.planTier} />,
  },
  {
    header: "Status",
    render: (row) => (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
          STATUS_COLOR[row.subscriptionStatus] || "bg-gray-100 text-gray-500"
        }`}
      >
        {row.subscriptionStatus}
      </span>
    ),
  },
  {
    header: "Next billing date",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">
        {row.nextBillingDate ? formatDateTime(row.nextBillingDate) : "—"}
      </span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Invoices",
    align: "center",
    render: (row) => <span className="text-sm text-brand-text">{row.totalInvoices}</span>,
  },
  {
    header: "Total revenue",
    align: "right",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text">
        {row.totalInvoiceAmount > 0 ? `$${Number(row.totalInvoiceAmount).toFixed(2)}` : "—"}
      </span>
    ),
  },
];

const ASSET_INVENTORY_COLUMNS = [
  {
    header: "Asset ID",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.assetId}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Owner name",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text">{row.ownerName}</span>
    ),
  },
  {
    header: "Owner email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[240px] truncate" title={row.ownerEmail}>
        {row.ownerEmail}
      </span>
    ),
  },
  {
    header: "Asset title",
    render: (row) => (
      <span className="text-sm text-brand-text">{row.softwareName}</span>
    ),
  },
  {
    header: "Analysis status",
    render: (row) => (
      <Badge variant={ASSET_STATUS_BADGE_VARIANT[row.analysisStatus] || "muted"}>
        {toTitle(row.analysisStatus)}
      </Badge>
    ),
  },
  {
    header: "Privacy URL",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[240px] truncate" title={row.privacyUrl}>
        {row.privacyUrl || "—"}
      </span>
    ),
  },
  {
    header: "Created at",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.createdAt)}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Updated at",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.updatedAt)}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
];

const ANALYSIS_PIPELINE_COLUMNS = [
  {
    header: "Job ID",
    render: (row) => <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.jobId}</span>,
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Job name",
    render: (row) => <span className="text-sm text-brand-text whitespace-nowrap">{row.jobName || "—"}</span>,
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Owner name",
    render: (row) => <span className="text-sm text-brand-text">{row.ownerName || "—"}</span>,
  },
  {
    header: "Owner email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[220px] truncate" title={row.ownerEmail}>
        {row.ownerEmail || "—"}
      </span>
    ),
  },
  {
    header: "Status",
    render: (row) => (
      <Badge variant={ANALYSIS_STATUS_BADGE_VARIANT[row.status] || "muted"}>
        {toTitle(row.status || "")}
      </Badge>
    ),
  },
  {
    header: "Created at",
    render: (row) => <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Started at",
    render: (row) => <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.startedAt)}</span>,
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Finished at",
    render: (row) => <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.finishedAt)}</span>,
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Duration (seconds)",
    align: "right",
    render: (row) => <span className="text-sm text-brand-text">{row.durationSeconds ?? "—"}</span>,
  },
];

const REPORT_GEN_VOLUME_COLUMNS = [
  {
    header: "Asset ID",
    render: (row) => (
      <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.assetId}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
  {
    header: "Asset title",
    render: (row) => (
      <span className="text-sm text-brand-text block max-w-[200px] truncate" title={row.assetTitle}>
        {row.assetTitle}
      </span>
    ),
  },
  {
    header: "Owner name",
    render: (row) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#ECE9FF] text-[#5B4FCF] text-xs font-semibold flex items-center justify-center shrink-0">
          {getInitials(row.ownerName)}
        </div>
        <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.ownerName}</span>
      </div>
    ),
  },
  {
    header: "Owner email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[220px] truncate" title={row.ownerEmail}>
        {row.ownerEmail}
      </span>
    ),
  },
  {
    header: "Total revisions",
    align: "right",
    render: (row) => <span className="text-sm font-semibold text-brand-text">{row.totalRevisions ?? "—"}</span>,
  },
  {
    header: "Latest revision",
    render: (row) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{row.latestRevisionDate || "—"}</span>
    ),
    cellClassName: "whitespace-nowrap",
  },
];

const PLAN_VS_USAGE_COLUMNS = [
  {
    header: "Name",
    render: (row) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#ECE9FF] text-[#5B4FCF] text-xs font-semibold flex items-center justify-center shrink-0">
          {getInitials(row.name)}
        </div>
        <span className="text-sm font-medium text-brand-text whitespace-nowrap">{row.name}</span>
      </div>
    ),
  },
  {
    header: "Email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[220px] truncate" title={row.email}>
        {row.email}
      </span>
    ),
  },
  {
    header: "Plan tier",
    render: (row) => <PlanBadge plan={row.planTier} />,
  },
  {
    header: "Asset limit",
    align: "right",
    render: (row) => (
      <span className="text-sm text-brand-text">{row.assetLimit != null ? row.assetLimit : "—"}</span>
    ),
  },
  {
    header: "Assets in use",
    align: "right",
    render: (row) => <span className="text-sm text-brand-text">{row.currentAssetCount ?? "—"}</span>,
  },
  {
    header: "Asset usage %",
    align: "right",
    render: (row) => {
      const pct = row.assetUsagePercent;
      if (pct == null) return <span className="text-sm text-brand-muted">—</span>;
      const over = pct > 100;
      return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
          over ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"
        }`}>
          {pct.toFixed(1)}%
        </span>
      );
    },
  },
  {
    header: "Analysis limit",
    align: "right",
    render: (row) => (
      <span className="text-sm text-brand-text">{row.analysisLimit != null ? row.analysisLimit : "—"}</span>
    ),
  },
  {
    header: "Completed analyses",
    align: "right",
    render: (row) => <span className="text-sm text-brand-text">{row.completedAnalyses ?? "—"}</span>,
  },
  {
    header: "Total revisions",
    align: "right",
    render: (row) => <span className="text-sm text-brand-text">{row.totalRevisions ?? "—"}</span>,
  },
];

const POWER_USERS_COLUMNS = [
  {
    header: "Name",
    render: (row) => <span className="text-sm text-brand-text">{row.name}</span>,
  },
  {
    header: "Email",
    render: (row) => (
      <span className="text-sm text-brand-muted block max-w-[260px] truncate" title={row.email}>
        {row.email}
      </span>
    ),
  },
  {
    header: "Rank",
    align: "right",
    render: (row) => <span className="text-sm text-brand-text">{row.rank ?? "—"}</span>,
  },
  {
    header: "Assets",
    align: "right",
    render: (row) => <span className="text-sm text-brand-text">{row.assets}</span>,
  },
  {
    header: "Revisions",
    align: "right",
    render: (row) => <span className="text-sm text-brand-text">{row.revisions}</span>,
  },
  {
    header: "Latest revision",
    render: (row) => <span className="text-sm text-brand-muted whitespace-nowrap">{formatDateTime(row.latestRevisionDate)}</span>,
    cellClassName: "whitespace-nowrap",
  },
];

// Prepends a serial-number column; page/pageSize keep numbers correct across pages
const withSerial = (cols, page = 1, pageSize = 1) => [
  {
    header: "S.No",
    render: (_, idx) => (
      <span className="text-sm text-brand-muted">{(page - 1) * pageSize + idx + 1}</span>
    ),
  },
  ...cols,
];

const AdminReportsPage = () => {
  const [searchParams] = useSearchParams();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [startValue, setStartValue] = useState("");
  const [endValue, setEndValue] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");
  const [appliedStart, setAppliedStart] = useState("");
  const [appliedEnd, setAppliedEnd] = useState("");
  const [userSignupPage, setUserSignupPage] = useState(1);
  const [userSignupTotal, setUserSignupTotal] = useState(0);
  const [planMixPage, setPlanMixPage] = useState(1);
  const [planMixData, setPlanMixData] = useState([]);
  const [planMixTotal, setPlanMixTotal] = useState(0);
  const [planMixLoading, setPlanMixLoading] = useState(false);
  const [assetInventoryPage, setAssetInventoryPage] = useState(1);
  const [assetInventoryData, setAssetInventoryData] = useState([]);
  const [assetInventoryTotal, setAssetInventoryTotal] = useState(0);
  const [assetInventoryLoading, setAssetInventoryLoading] = useState(false);
  const [analysisPipelinePage, setAnalysisPipelinePage] = useState(1);
  const [analysisPipelineData, setAnalysisPipelineData] = useState([]);
  const [analysisPipelineTotal, setAnalysisPipelineTotal] = useState(0);
  const [analysisPipelineLoading, setAnalysisPipelineLoading] = useState(false);
  const [powerUsersPage, setPowerUsersPage] = useState(1);
  const [powerUsersData, setPowerUsersData] = useState([]);
  const [powerUsersTotal, setPowerUsersTotal] = useState(0);
  const [powerUsersLoading, setPowerUsersLoading] = useState(false);
  const [planVsUsagePage, setPlanVsUsagePage] = useState(1);
  const [planVsUsageData, setPlanVsUsageData] = useState([]);
  const [planVsUsageTotal, setPlanVsUsageTotal] = useState(0);
  const [planVsUsageLoading, setPlanVsUsageLoading] = useState(false);
  const [planVsUsageSummary, setPlanVsUsageSummary] = useState(null);
  const [reportGenVolumeSummary, setReportGenVolumeSummary] = useState(null);
  const [reportGenVolumeDailyVolume, setReportGenVolumeDailyVolume] = useState([]);
  const [reportGenVolumeTopAssets, setReportGenVolumeTopAssets] = useState([]);
  const [reportGenVolumeLoading, setReportGenVolumeLoading] = useState(false);
  const [staleAssetsData, setStaleAssetsData] = useState([]);
  const [staleAssetsPage, setStaleAssetsPage] = useState(1);
  const [staleJobsData, setStaleJobsData] = useState([]);
  const [staleOrStuckSummary, setStaleOrStuckSummary] = useState(null);
  const [staleOrStuckLoading, setStaleOrStuckLoading] = useState(false);
  const [enterpriseFunnelPage, setEnterpriseFunnelPage] = useState(1);
  const [enterpriseFunnelData, setEnterpriseFunnelData] = useState([]);
  const [enterpriseFunnelTotal, setEnterpriseFunnelTotal] = useState(0);
  const [enterpriseFunnelSummary, setEnterpriseFunnelSummary] = useState(null);
  const [enterpriseFunnelLoading, setEnterpriseFunnelLoading] = useState(false);
  const [activityAuditPage, setActivityAuditPage] = useState(1);
  const [activityAuditData, setActivityAuditData] = useState([]);
  const [activityAuditTotal, setActivityAuditTotal] = useState(0);
  const [activityAuditLoading, setActivityAuditLoading] = useState(false);

  const selectedReport = searchParams.get("view") || REPORT_OPTIONS[0].key;
  const currentReport = REPORT_OPTIONS.find((item) => item.key === selectedReport) || REPORT_OPTIONS[0];

  const isUserSignupReport = selectedReport === "user-signup-overview";
  const isPlanMixReport = selectedReport === "plan-mix-billing-signal";
  const isAssetInventoryReport = selectedReport === "asset-inventory";
  const isAnalysisPipelineReport = selectedReport === "analysis-pipeline-health";
  const isPowerUsersReport = selectedReport === "power-users";
  const isPlanVsUsageReport = selectedReport === "plan-vs-usage";
  const isReportGenVolumeReport = selectedReport === "report-generation-volume";
  const isStaleOrStuckReport = selectedReport === "stale-or-stuck-work";
  const isEnterpriseFunnelReport = selectedReport === "enterprise-funnel";
  const isActivityAuditReport = selectedReport === "activity-audit-trail";

  useEffect(() => {
    // Reset date range whenever the active report changes
    setStartValue(""); setEndValue(""); setDateRangeError("");
    setAppliedStart(""); setAppliedEnd("");
    setStaleAssetsPage(1);

    if (isUserSignupReport) setUserSignupPage(1);
    if (isPlanMixReport) setPlanMixPage(1);
    if (isAssetInventoryReport) setAssetInventoryPage(1);
    if (isAnalysisPipelineReport) setAnalysisPipelinePage(1);
    if (isPowerUsersReport) setPowerUsersPage(1);
    if (isPlanVsUsageReport) setPlanVsUsagePage(1);
    if (isStaleOrStuckReport) { /* no pagination for stale report */ }
    if (isEnterpriseFunnelReport) setEnterpriseFunnelPage(1);
    if (isActivityAuditReport) setActivityAuditPage(1);
  }, [isUserSignupReport, isPlanMixReport, isAssetInventoryReport, isAnalysisPipelineReport, isPowerUsersReport, isPlanVsUsageReport, isReportGenVolumeReport, isStaleOrStuckReport, isEnterpriseFunnelReport, isActivityAuditReport]);

  useEffect(() => {
    if (!isPlanMixReport) return;
    if (!appliedStart) {
      setPlanMixData([]);
      setPlanMixTotal(0);
      return;
    }
    let cancelled = false;
    setPlanMixLoading(true);
    setError("");

    adminService
      .getPlanMixReport({ page: planMixPage, limit: PLAN_MIX_PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const total =
          payload?.pagination?.totalRows ??
          payload?.pagination?.total ??
          payload?.total ??
          rows.length;

        setPlanMixData(
          rows.map((row, i) => ({
            id: row.userId || row.user_id || row.id || i,
            name: row.userName || row.user_name || row.name || "—",
            email: row.email || "—",
            planTier: row.planTier || row.plan_tier || "—",
            subscriptionStatus: row.subscriptionStatus || row.subscription_status || "inactive",
            nextBillingDate: row.nextBillingDate || row.next_billing_date || null,
            totalInvoices: row.totalInvoices ?? row.total_invoices ?? 0,
            totalInvoiceAmount: row.totalInvoiceAmount ?? row.total_invoice_amount ?? 0,
          }))
        );
        setPlanMixTotal(total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch plan mix report.");
      })
      .finally(() => {
        if (!cancelled) setPlanMixLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isPlanMixReport, planMixPage, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isAssetInventoryReport) return;
    if (!appliedStart) {
      setAssetInventoryData([]);
      setAssetInventoryTotal(0);
      return;
    }
    let cancelled = false;
    setAssetInventoryLoading(true);
    setError("");

    adminService
      .getAssetInventoryReport({ page: assetInventoryPage, limit: ASSET_INVENTORY_PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const total =
          payload?.pagination?.totalRows ??
          payload?.pagination?.total ??
          payload?.total ??
          rows.length;

        setAssetInventoryData(
          rows.map((row, i) => ({
            id: row.assetId || row.asset_id || row.id || i,
            assetId: row.assetId || row.asset_id || row.id || "—",
            ownerName: row.ownerName || row.owner_name || row.userName || row.user_name || "—",
            ownerEmail: row.ownerEmail || row.owner_email || row.email || "—",
            softwareName: row.assetTitle || row.asset_title || row.softwareName || row.software_name || row.assetName || row.asset_name || "—",
            analysisStatus: row.assetStatus || row.asset_status || row.analysisStatus || row.analysis_status || "processing",
            privacyUrl: row.policyUrl || row.policy_url || row.privacyUrl || row.privacy_url || "",
            createdAt: row.assignedDate || row.assigned_date || row.createdAt || row.created_at || null,
            updatedAt: row.lastUpdatedDate || row.last_updated_date || row.updatedAt || row.updated_at || null,
          }))
        );
        setAssetInventoryTotal(total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch asset inventory report.");
      })
      .finally(() => {
        if (!cancelled) setAssetInventoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAssetInventoryReport, assetInventoryPage, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isAnalysisPipelineReport) return;
    if (!appliedStart) {
      setAnalysisPipelineData([]);
      setAnalysisPipelineTotal(0);
      return;
    }
    let cancelled = false;
    setAnalysisPipelineLoading(true);
    setError("");

    adminService
      .getAnalysisPipelineReport({ page: analysisPipelinePage, limit: ANALYSIS_PIPELINE_PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const total =
          payload?.pagination?.totalRows ??
          payload?.pagination?.total ??
          payload?.total ??
          rows.length;

        setAnalysisPipelineData(
          rows.map((row, i) => ({
            id: row.jobId || row.job_id || i,
            jobId: row.jobId || row.job_id || "—",
            jobName: row.jobName || row.job_name || row.assetTitle || row.asset_title || "—",
            ownerName: row.ownerName || row.owner_name || "—",
            ownerEmail: row.ownerEmail || row.owner_email || "—",
            status: row.status || "queued",
            createdAt: row.createdDate || row.created_date || row.createdAt || row.created_at || null,
            startedAt: row.startedDate || row.started_date || row.startedAt || row.started_at || null,
            finishedAt: row.completedDate || row.completed_date || row.finishedAt || row.finished_at || null,
            durationSeconds: row.duration ?? row.durationSeconds ?? row.duration_seconds ?? null,
          }))
        );
        setAnalysisPipelineTotal(total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch analysis pipeline report.");
      })
      .finally(() => {
        if (!cancelled) setAnalysisPipelineLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAnalysisPipelineReport, analysisPipelinePage, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isPowerUsersReport) return;
    if (!appliedStart) {
      setPowerUsersData([]);
      setPowerUsersTotal(0);
      return;
    }
    let cancelled = false;
    setPowerUsersLoading(true);
    setError("");

    adminService
      .getPowerUsersReport({ page: powerUsersPage, limit: POWER_USERS_PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const total =
          payload?.pagination?.totalRows ??
          payload?.pagination?.total ??
          payload?.total ??
          rows.length;

        setPowerUsersData(
          rows.map((row, i) => ({
            id: row.userId || row.user_id || i,
            rank: row.rank ?? i + 1,
            userId: row.userId || row.user_id || "—",
            name: row.userName || row.user_name || row.name || "—",
            email: row.userEmail || row.user_email || row.email || "—",
            assets: row.totalAssets ?? row.total_assets ?? 0,
            revisions: row.totalRevisions ?? row.total_revisions ?? 0,
            latestRevisionDate: row.latestRevisionDate || row.latest_revision_date || null,
          }))
        );
        setPowerUsersTotal(total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch power users report.");
      })
      .finally(() => {
        if (!cancelled) setPowerUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isPowerUsersReport, powerUsersPage, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isPlanVsUsageReport) return;
    if (!appliedStart) {
      setPlanVsUsageData([]);
      setPlanVsUsageTotal(0);
      setPlanVsUsageSummary(null);
      return;
    }
    let cancelled = false;
    setPlanVsUsageLoading(true);
    setError("");

    adminService
      .getPlanVsUsageReport({ page: planVsUsagePage, limit: PLAN_VS_USAGE_PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const total =
          payload?.pagination?.totalRows ??
          payload?.pagination?.total ??
          payload?.total ??
          rows.length;

        if (payload?.summary) setPlanVsUsageSummary(payload.summary);
        setPlanVsUsageData(
          rows.map((row, i) => ({
            id: row.userId || i,
            name: row.userName || "—",
            email: row.userEmail || "—",
            planTier: row.planTier || "—",
            assetLimit: row.assetLimit ?? null,
            analysisLimit: row.analysisLimit ?? null,
            revisionLimit: row.revisionLimit ?? null,
            currentAssetCount: row.currentAssetCount ?? 0,
            completedAnalyses: row.completedAnalyses ?? 0,
            totalRevisions: row.totalRevisions ?? 0,
            assetUsagePercent: row.assetUsagePercent ?? null,
            analysisUsagePercent: row.analysisUsagePercent ?? null,
            revisionUsagePercent: row.revisionUsagePercent ?? null,
          }))
        );
        setPlanVsUsageTotal(total);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch plan vs usage report.");
      })
      .finally(() => {
        if (!cancelled) setPlanVsUsageLoading(false);
      });

    return () => { cancelled = true; };
  }, [isPlanVsUsageReport, planVsUsagePage, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isReportGenVolumeReport) return;
    if (!appliedStart) {
      setReportGenVolumeSummary(null);
      setReportGenVolumeDailyVolume([]);
      setReportGenVolumeTopAssets([]);
      return;
    }
    let cancelled = false;
    setReportGenVolumeLoading(true);
    setError("");

    adminService
      .getReportGenerationVolumeReport({ startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        setReportGenVolumeSummary(payload?.summary ?? null);
        setReportGenVolumeDailyVolume(Array.isArray(payload?.dailyVolume) ? payload.dailyVolume : []);
        setReportGenVolumeTopAssets(
          (Array.isArray(payload?.topAssets) ? payload.topAssets : []).map((row, i) => ({
            id: row.assetId || i,
            assetId: row.assetId || "—",
            assetTitle: row.assetTitle || "—",
            ownerName: row.ownerName || "—",
            ownerEmail: row.ownerEmail || "—",
            totalRevisions: row.totalRevisions ?? "—",
            latestRevisionDate: row.latestRevisionDate || null,
          }))
        );
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch report generation volume report.");
      })
      .finally(() => {
        if (!cancelled) setReportGenVolumeLoading(false);
      });

    return () => { cancelled = true; };
  }, [isReportGenVolumeReport, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isStaleOrStuckReport) return;
    if (!appliedStart) {
      setStaleOrStuckSummary(null);
      setStaleAssetsData([]);
      setStaleJobsData([]);
      return;
    }
    let cancelled = false;
    setStaleOrStuckLoading(true);
    setError("");
    adminService
      .getStaleOrStuckReport({ startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        setStaleOrStuckSummary(payload?.summary ?? null);
        setStaleAssetsData(
          (Array.isArray(payload?.staleAssets) ? payload.staleAssets : []).map((row, i) => ({
            id: row.assetId || i,
            assetId: row.assetId || "—",
            assetTitle: row.assetTitle || "—",
            ownerName: row.ownerName || "—",
            ownerEmail: row.ownerEmail || "—",
            status: row.status || "—",
            lastUpdatedDate: row.lastUpdatedDate || null,
            staleDuration: row.staleDuration || "—",
          }))
        );
        setStaleJobsData(
          (Array.isArray(payload?.staleJobs) ? payload.staleJobs : []).map((row, i) => ({
            id: row.jobId || i,
            jobId: row.jobId || "—",
            jobName: row.jobName || "—",
            ownerName: row.ownerName || "—",
            ownerEmail: row.ownerEmail || "—",
            status: row.status || "—",
            createdDate: row.createdDate || null,
            staleDuration: row.staleDuration || "—",
          }))
        );
      })
      .catch((err) => { if (!cancelled) setError(err.message || "Failed to fetch stale or stuck report."); })
      .finally(() => { if (!cancelled) setStaleOrStuckLoading(false); });
    return () => { cancelled = true; };
  }, [isStaleOrStuckReport, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isEnterpriseFunnelReport) return;
    if (!appliedStart) {
      setEnterpriseFunnelData([]);
      setEnterpriseFunnelTotal(0);
      setEnterpriseFunnelSummary(null);
      return;
    }
    let cancelled = false;
    setEnterpriseFunnelLoading(true);
    setError("");
    adminService
      .getEnterpriseFunnelReport({ page: enterpriseFunnelPage, limit: ENTERPRISE_FUNNEL_PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const total = payload?.pagination?.totalRows ?? payload?.pagination?.total ?? payload?.total ?? rows.length;
        setEnterpriseFunnelSummary(payload?.summary ?? null);
        setEnterpriseFunnelData(
          rows.map((row, i) => ({
            id: row.inquiryId || row.id || i,
            inquiryId: row.inquiryId || row.id || "—",
            contactName: row.contactName || row.name || "—",
            contactEmail: row.contactEmail || row.email || "—",
            companyName: row.companyName || row.company || "—",
            status: row.status || "—",
            assignedOwner: row.assignedOwner || "—",
            createdDate: row.createdDate || row.createdAt || null,
          }))
        );
        setEnterpriseFunnelTotal(total);
      })
      .catch((err) => { if (!cancelled) setError(err.message || "Failed to fetch enterprise funnel report."); })
      .finally(() => { if (!cancelled) setEnterpriseFunnelLoading(false); });
    return () => { cancelled = true; };
  }, [isEnterpriseFunnelReport, enterpriseFunnelPage, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isActivityAuditReport) return;
    if (!appliedStart) {
      setActivityAuditData([]);
      setActivityAuditTotal(0);
      return;
    }
    let cancelled = false;
    setActivityAuditLoading(true);
    setError("");
    adminService
      .getActivityAuditReport({ page: activityAuditPage, limit: ACTIVITY_AUDIT_PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const total = payload?.pagination?.totalRows ?? payload?.pagination?.total ?? payload?.total ?? rows.length;
        setActivityAuditData(
          rows.map((row, i) => ({
            id: row.activityId || i,
            activityId: row.activityId || row.sNo || "\u2014",
            activityType: row.activityType || "\u2014",
            activityStatus: row.activityStatus || "\u2014",
            activityTimestamp: row.activityTimestamp || null,
            userName: row.userName || row.user_name || "\u2014",
            userEmail: row.userEmail || row.user_email || "\u2014",
            userId: row.userId ?? row.user_id ?? "\u2014",
            entityType: row.entityType || row.entity_type || "\u2014",
            entityId: row.entityId ?? row.entity_id ?? "\u2014",
          }))
        );
        setActivityAuditTotal(total);
      })
      .catch((err) => { if (!cancelled) setError(err.message || "Failed to fetch activity audit trail report."); })
      .finally(() => { if (!cancelled) setActivityAuditLoading(false); });
    return () => { cancelled = true; };
  }, [isActivityAuditReport, activityAuditPage, appliedStart, appliedEnd]);

  useEffect(() => {
    if (!isUserSignupReport) {
      setError("");
      setReportData(null);
      return;
    }
    if (!appliedStart) {
      setReportData(null);
      setUserSignupTotal(0);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    adminService
      .getUserSignupOverviewReport({ page: userSignupPage, limit: PAGE_SIZE, startDate: appliedStart, endDate: appliedEnd })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res;
        const total =
          payload?.pagination?.totalRows ??
          payload?.pagination?.total ??
          payload?.total ??
          payload?.summary?.total_users ??
          0;
        setUserSignupTotal(total);
        setReportData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to fetch report data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isUserSignupReport, userSignupPage, appliedStart, appliedEnd]);

  const tableRows = useMemo(() => {
    const apiRows = reportData?.table?.rows;
    if (!Array.isArray(apiRows)) return [];

    return apiRows.map((row, index) => ({
      id: row.user_id || index,
      name: row.user_name || "—",
      email: row.user_email || "—",
      role: row.role || "—",
      planTier: row.plan_tier || "—",
      planStatus: row.plan_status || "—",
      emailVerified: Boolean(row.email_verified),
      isActive: row.is_active !== undefined ? Boolean(row.is_active) : null,
      isPaid: row.is_paid !== undefined ? Boolean(row.is_paid) : null,
      company: row.company_name || "—",
      lastLogin: row.last_login || null,
      createdAt: row.created_at,
    }));
  }, [reportData]);

  // Server-side pagination: tableRows is already the current page of results
  const pagedUserSignupRows = tableRows;

  const userSignupTotalPages = Math.max(1, Math.ceil((userSignupTotal || tableRows.length) / PAGE_SIZE));
  const planMixTotalPages = Math.max(1, Math.ceil(planMixTotal / PLAN_MIX_PAGE_SIZE));
  const assetInventoryTotalPages = Math.max(1, Math.ceil(assetInventoryTotal / ASSET_INVENTORY_PAGE_SIZE));
  const analysisPipelineTotalPages = Math.max(1, Math.ceil(analysisPipelineTotal / ANALYSIS_PIPELINE_PAGE_SIZE));
  const powerUsersTotalPages = Math.max(1, Math.ceil(powerUsersTotal / POWER_USERS_PAGE_SIZE));
  const planVsUsageTotalPages = Math.max(1, Math.ceil(planVsUsageTotal / PLAN_VS_USAGE_PAGE_SIZE));
  const enterpriseFunnelTotalPages = Math.max(1, Math.ceil(enterpriseFunnelTotal / ENTERPRISE_FUNNEL_PAGE_SIZE));
  const activityAuditTotalPages = Math.max(1, Math.ceil(activityAuditTotal / ACTIVITY_AUDIT_PAGE_SIZE));
  const staleAssetsTotalPages = Math.max(1, Math.ceil(staleAssetsData.length / STALE_ASSETS_PAGE_SIZE));
  const pagedStaleAssetsRows = staleAssetsData.slice((staleAssetsPage - 1) * STALE_ASSETS_PAGE_SIZE, staleAssetsPage * STALE_ASSETS_PAGE_SIZE);

  // Adds one day so the end date is inclusive (backend treats date-only as start-of-day)
  const nextDay = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };

  const handleApplyDates = () => {
    if (!startValue && !endValue) {
      setAppliedStart(""); setAppliedEnd(""); setDateRangeError("");
      setUserSignupPage(1); setPlanMixPage(1); setAssetInventoryPage(1); setAnalysisPipelinePage(1);
      setPowerUsersPage(1); setPlanVsUsagePage(1);
      setEnterpriseFunnelPage(1); setActivityAuditPage(1); setStaleAssetsPage(1);
      return;
    }
    if (startValue && !endValue) { setDateRangeError("Please select an end date."); return; }
    if (!startValue && endValue) { setDateRangeError("Please select a start date."); return; }
    if (startValue && endValue) {
      const diff = (new Date(endValue) - new Date(startValue)) / (1000 * 60 * 60 * 24);
      if (diff < 0) { setDateRangeError("End date must be after start date."); return; }
      if (diff > 90) { setDateRangeError("Date range cannot exceed 90 days."); return; }
    }
    setDateRangeError("");
    setAppliedStart(startValue); setAppliedEnd(nextDay(endValue));
    setUserSignupPage(1); setPlanMixPage(1); setAssetInventoryPage(1); setAnalysisPipelinePage(1);
    setPowerUsersPage(1); setPlanVsUsagePage(1);
    setEnterpriseFunnelPage(1); setActivityAuditPage(1); setStaleAssetsPage(1);
  };

  const handleClearDates = () => {
    setStartValue(""); setEndValue(""); setDateRangeError("");
    setAppliedStart(""); setAppliedEnd("");
    setUserSignupPage(1); setPlanMixPage(1); setAssetInventoryPage(1); setAnalysisPipelinePage(1);
    setPowerUsersPage(1); setPlanVsUsagePage(1);
    setEnterpriseFunnelPage(1); setActivityAuditPage(1); setStaleAssetsPage(1);
  };

  const handleDownloadExcel = async () => {
    if ((!isUserSignupReport && !isPlanMixReport && !isAssetInventoryReport && !isAnalysisPipelineReport && !isPowerUsersReport && !isPlanVsUsageReport && !isReportGenVolumeReport && !isStaleOrStuckReport && !isEnterpriseFunnelReport && !isActivityAuditReport) || isDownloading) return;
    // Use the live input values so download respects whatever dates are currently typed,
    // even if the user hasn't clicked Apply yet.
    const dlStart = startValue;
    const dlEnd = nextDay(endValue);

    try {
      setIsDownloading(true);
      setError("");

      if (isPlanMixReport) {
        const { blob, filename } = await adminService.downloadPlanMixExport({ startDate: dlStart, endDate: dlEnd });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      if (isAssetInventoryReport) {
        const allRows = [];

        const firstResponse = await adminService.getAssetInventoryReport({
          page: 1,
          limit: ASSET_INVENTORY_PAGE_SIZE,
          startDate: dlStart,
          endDate: dlEnd,
        });
        const firstPayload = firstResponse?.data ?? firstResponse;
        const firstRows = Array.isArray(firstPayload?.rows) ? firstPayload.rows : [];
        const totalPages = firstPayload?.pagination?.totalPages || 1;
        allRows.push(...firstRows);

        for (let page = 2; page <= totalPages; page += 1) {
          const response = await adminService.getAssetInventoryReport({
            page,
            limit: ASSET_INVENTORY_PAGE_SIZE,
            startDate: dlStart,
            endDate: dlEnd,
          });
          const payload = response?.data ?? response;
          const rows = Array.isArray(payload?.rows) ? payload.rows : [];
          allRows.push(...rows);
        }

        const excelRows = allRows.map((row) => ({
          "Asset ID": row.assetId || row.asset_id || row.id || "",
          "Asset title": row.assetTitle || row.asset_title || row.softwareName || row.software_name || row.assetName || row.asset_name || "",
          "Asset status": toTitle(row.assetStatus || row.asset_status || row.analysisStatus || row.analysis_status || ""),
          "Asset type": row.assetType || row.asset_type || "url",
          "Owner name": row.ownerName || row.owner_name || row.userName || row.user_name || "",
          "Owner email": row.ownerEmail || row.owner_email || row.email || "",
          "Policy URL": row.policyUrl || row.policy_url || row.privacyUrl || row.privacy_url || "",
          "Assigned date": row.assignedDate || row.assigned_date || row.createdAt || row.created_at || "",
          "Last updated date": row.lastUpdatedDate || row.last_updated_date || row.updatedAt || row.updated_at || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Asset Inventory");
        XLSX.writeFile(workbook, "asset-inventory.xlsx");
        return;
      }

      if (isAnalysisPipelineReport) {
        const allRows = [];

        const firstResponse = await adminService.getAnalysisPipelineReport({
          page: 1,
          limit: ANALYSIS_PIPELINE_PAGE_SIZE,
          startDate: dlStart,
          endDate: dlEnd,
        });
        const firstPayload = firstResponse?.data ?? firstResponse;
        const firstRows = Array.isArray(firstPayload?.rows) ? firstPayload.rows : [];
        const totalPages = firstPayload?.pagination?.totalPages || 1;
        allRows.push(...firstRows);

        for (let page = 2; page <= totalPages; page += 1) {
          const response = await adminService.getAnalysisPipelineReport({
            page,
            limit: ANALYSIS_PIPELINE_PAGE_SIZE,
            startDate: dlStart,
            endDate: dlEnd,
          });
          const payload = response?.data ?? response;
          const rows = Array.isArray(payload?.rows) ? payload.rows : [];
          allRows.push(...rows);
        }

        const excelRows = allRows.map((row) => ({
          "Job ID": row.jobId || row.job_id || "",
          "Job name": row.jobName || row.job_name || row.assetTitle || row.asset_title || "",
          "Owner name": row.ownerName || row.owner_name || "",
          "Owner email": row.ownerEmail || row.owner_email || "",
          "Status": toTitle(row.status || ""),
          "Created at": row.createdDate || row.created_date || row.createdAt || row.created_at || "",
          "Started at": row.startedDate || row.started_date || row.startedAt || row.started_at || "",
          "Finished at": row.completedDate || row.completed_date || row.finishedAt || row.finished_at || "",
          "Duration (seconds)": row.duration ?? row.durationSeconds ?? row.duration_seconds ?? "",
          "Failure reason": row.failureReason || row.failure_reason || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows.length ? excelRows : [{
          "Job ID": "",
          "Job name": "",
          "Owner name": "",
          "Owner email": "",
          "Status": "",
          "Created at": "",
          "Started at": "",
          "Finished at": "",
          "Duration (seconds)": "",
          "Failure reason": "",
        }]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Analysis Pipeline Health");
        XLSX.writeFile(workbook, "analysis-pipeline-health.xlsx");
        return;
      }

      if (isPowerUsersReport) {
        const allRows = [];

        const firstResponse = await adminService.getPowerUsersReport({
          page: 1,
          limit: POWER_USERS_PAGE_SIZE,
          startDate: dlStart,
          endDate: dlEnd,
        });
        const firstPayload = firstResponse?.data ?? firstResponse;
        const firstRows = Array.isArray(firstPayload?.rows) ? firstPayload.rows : [];
        const totalPages = firstPayload?.pagination?.totalPages || 1;
        allRows.push(...firstRows);

        for (let page = 2; page <= totalPages; page += 1) {
          const response = await adminService.getPowerUsersReport({
            page,
            limit: POWER_USERS_PAGE_SIZE,
            startDate: dlStart,
            endDate: dlEnd,
          });
          const payload = response?.data ?? response;
          const rows = Array.isArray(payload?.rows) ? payload.rows : [];
          allRows.push(...rows);
        }

        const excelRows = allRows.map((row, index) => ({
          "Rank": row.rank ?? index + 1,
          "User ID": row.userId || row.user_id || "",
          "Name": row.userName || row.user_name || row.name || "",
          "Email": row.userEmail || row.user_email || row.email || "",
          "Assets": row.totalAssets ?? row.total_assets ?? 0,
          "Revisions": row.totalRevisions ?? row.total_revisions ?? 0,
          "Latest revision": row.latestRevisionDate || row.latest_revision_date || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Power Users");
        XLSX.writeFile(workbook, "power-users.xlsx");
        return;
      }

      if (isActivityAuditReport) {
        const allRows = [];
        const firstResponse = await adminService.getActivityAuditReport({ page: 1, limit: ACTIVITY_AUDIT_PAGE_SIZE, startDate: dlStart, endDate: dlEnd });
        const firstPayload = firstResponse?.data ?? firstResponse;
        const firstRows = Array.isArray(firstPayload?.rows) ? firstPayload.rows : [];
        const totalPages = firstPayload?.pagination?.totalPages || 1;
        allRows.push(...firstRows);
        for (let page = 2; page <= totalPages; page++) {
          const r = await adminService.getActivityAuditReport({ page, limit: ACTIVITY_AUDIT_PAGE_SIZE, startDate: dlStart, endDate: dlEnd });
          const p = r?.data ?? r;
          allRows.push(...(Array.isArray(p?.rows) ? p.rows : []));
        }
        const excelRows = allRows.map((row) => ({
          "Activity type": row.activityType || "",
          "User name": row.userName || row.user_name || "",
          "User email": row.userEmail || row.user_email || "",
          "User ID": row.userId ?? row.user_id ?? "",
          "Entity type": row.entityType || row.entity_type || "",
          "Status": row.activityStatus || "",
          "Timestamp": row.activityTimestamp || "",
        }));
        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Activity Audit Trail");
        XLSX.writeFile(workbook, "activity-audit-trail.xlsx");
        return;
      }

      if (isEnterpriseFunnelReport) {
        const blob = await adminService.downloadEnterpriseFunnelExport({ startDate: dlStart, endDate: dlEnd });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "enterprise-funnel.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      if (isStaleOrStuckReport) {
        const res = await adminService.getStaleOrStuckReport({ startDate: dlStart, endDate: dlEnd });
        const payload = res?.data ?? res;
        const allAssets = Array.isArray(payload?.staleAssets) ? payload.staleAssets : [];
        const allJobs = Array.isArray(payload?.staleJobs) ? payload.staleJobs : [];
        const workbook = XLSX.utils.book_new();
        const assetSheet = XLSX.utils.json_to_sheet(allAssets.map((row) => ({
          "Asset ID": row.assetId || "",
          "Asset title": row.assetTitle || "",
          "Owner name": row.ownerName || "",
          "Owner email": row.ownerEmail || "",
          "Status": toTitle(row.status || ""),
          "Last updated": row.lastUpdatedDate || "",
          "Stale duration": row.staleDuration || "",
        })));
        const jobSheet = XLSX.utils.json_to_sheet(allJobs.map((row) => ({
          "Job ID": row.jobId || "",
          "Job name": row.jobName || "",
          "Owner name": row.ownerName || "",
          "Owner email": row.ownerEmail || "",
          "Status": toTitle(row.status || ""),
          "Created at": row.createdDate || "",
          "Stale duration": row.staleDuration || "",
        })));
        XLSX.utils.book_append_sheet(workbook, assetSheet, "Stale Assets");
        XLSX.utils.book_append_sheet(workbook, jobSheet, "Stale Jobs");
        XLSX.writeFile(workbook, "stale-or-stuck-work.xlsx");
        return;
      }

      if (isReportGenVolumeReport) {
        const topAssetsRows = reportGenVolumeTopAssets.map((row) => ({
          "Asset ID": row.assetId || "",
          "Asset title": row.assetTitle || "",
          "Owner name": row.ownerName || "",
          "Owner email": row.ownerEmail || "",
          "Total revisions": row.totalRevisions ?? "",
          "Latest revision date": row.latestRevisionDate || "",
        }));
        const dailyRows = reportGenVolumeDailyVolume.map((d) => ({
          "Date": d.date || "",
          "Revision count": d.revisionCount ?? 0,
        }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(topAssetsRows), "Top Assets");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dailyRows), "Daily Volume");
        XLSX.writeFile(workbook, "report-generation-volume.xlsx");
        return;
      }

      if (isPlanVsUsageReport) {
        const allRows = [];
        const firstResponse = await adminService.getPlanVsUsageReport({ page: 1, limit: PLAN_VS_USAGE_PAGE_SIZE, startDate: dlStart, endDate: dlEnd });
        const firstPayload = firstResponse?.data ?? firstResponse;
        const firstRows = Array.isArray(firstPayload?.rows) ? firstPayload.rows : [];
        const totalPages = firstPayload?.pagination?.totalPages || 1;
        allRows.push(...firstRows);

        for (let page = 2; page <= totalPages; page += 1) {
          const response = await adminService.getPlanVsUsageReport({ page, limit: PLAN_VS_USAGE_PAGE_SIZE, startDate: dlStart, endDate: dlEnd });
          const payload = response?.data ?? response;
          const rows = Array.isArray(payload?.rows) ? payload.rows : [];
          allRows.push(...rows);
        }

        const excelRows = allRows.map((row) => ({
          "Name": row.userName || "",
          "Email": row.userEmail || "",
          "Plan tier": row.planTier || "",
          "Asset limit": row.assetLimit ?? "",
          "Analysis limit": row.analysisLimit ?? "",
          "Revision limit": row.revisionLimit ?? "",
          "Assets in use": row.currentAssetCount ?? "",
          "Completed analyses": row.completedAnalyses ?? "",
          "Total revisions": row.totalRevisions ?? "",
          "Asset usage %": row.assetUsagePercent != null ? `${row.assetUsagePercent}%` : "",
          "Analysis usage %": row.analysisUsagePercent != null ? `${row.analysisUsagePercent}%` : "",
          "Revision usage %": row.revisionUsagePercent != null ? `${row.revisionUsagePercent}%` : "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Plan vs Usage");
        XLSX.writeFile(workbook, "plan-vs-usage.xlsx");
        return;
      }

      // User Signup Overview — fetch all pages then export
      const allSignupRows = [];
      const firstSignupRes = await adminService.getUserSignupOverviewReport({
        page: 1,
        limit: PAGE_SIZE,
        startDate: dlStart,
        endDate: dlEnd,
      });
      const firstSignupPayload = firstSignupRes?.data ?? firstSignupRes;
      const firstSignupRows = Array.isArray(firstSignupPayload?.table?.rows) ? firstSignupPayload.table.rows : [];
      const signupTotalPages = Math.max(
        1,
        firstSignupPayload?.pagination?.totalPages ??
          Math.ceil(
            (firstSignupPayload?.pagination?.totalRows ??
              firstSignupPayload?.pagination?.total ??
              firstSignupPayload?.summary?.total_users ??
              firstSignupRows.length) / PAGE_SIZE
          )
      );
      allSignupRows.push(...firstSignupRows);
      for (let page = 2; page <= signupTotalPages; page++) {
        const r = await adminService.getUserSignupOverviewReport({ page, limit: PAGE_SIZE, startDate: dlStart, endDate: dlEnd });
        const p = r?.data ?? r;
        allSignupRows.push(...(Array.isArray(p?.table?.rows) ? p.table.rows : []));
      }

      const excelRows = allSignupRows.map((row) => ({
        "Name": row.user_name || "",
        "Email": row.user_email || "",
        "Role": toTitle(row.role || ""),
        "Plan tier": toTitle(row.plan_tier || ""),
        "Email verified": row.email_verified ? "Yes" : "No",
        "Paid": row.is_paid != null ? (row.is_paid ? "Yes" : "No") : "",
        "Company": row.company_name || "",
        "Created at": row.created_at || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "User Signup Overview");
      XLSX.writeFile(workbook, "user-signup-overview.xlsx");
    } catch (err) {
      setError(err.message || "Failed to download Excel file.");
    } finally {
      setIsDownloading(false);
    }
  };

  const summary = reportData?.summary;
  const totalUsers = summary?.total_users ?? tableRows.length;
  const verifiedUsers = summary?.verified_users ?? tableRows.filter((row) => row.emailVerified).length;
  const unverifiedUsers = summary?.unverified_users ?? Math.max(totalUsers - verifiedUsers, 0);
  const paidUsers = summary?.paid_users ?? null;
  const activeUsers = summary?.active_users ?? null;

  // Use server-provided plan breakdown (summary.users_by_plan) when available;
  // fall back to computing from table rows so Enterprise and any future tier appear correctly.
  const plansBreakdownText = (() => {
    const byPlan = summary?.users_by_plan;
    if (Array.isArray(byPlan) && byPlan.length > 0) {
      return byPlan
        .map((p) => `${p.plan_tier} ${p.user_count}`)
        .join(" • ");
    }
    // Fallback: count from rows (handles any tier dynamically)
    const counts = {};
    tableRows.forEach((row) => {
      const tier = row.planTier && row.planTier !== "—" ? row.planTier : "Unknown";
      counts[tier] = (counts[tier] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([tier, count]) => `${tier} ${count}`)
      .join(" • ") || "—";
  })();

  const userSignupMetrics = [
    { label: "Total Users", value: totalUsers },
    { label: "Verified", value: verifiedUsers },
    { label: "Unverified", value: unverifiedUsers },
    ...(paidUsers !== null ? [{ label: "Paid Users", value: paidUsers }] : []),
    ...(activeUsers !== null ? [{ label: "Active Users", value: activeUsers }] : []),
    { label: "Plans", value: plansBreakdownText, isText: true },
  ];

  const planMixRevenue = planMixData.reduce((sum, row) => sum + Number(row.totalInvoiceAmount || 0), 0);
  const planMixInvoices = planMixData.reduce((sum, row) => sum + Number(row.totalInvoices || 0), 0);
  const planMixMetrics = [
    { label: "Total Users", value: planMixTotal },
    {
      label: "Active",
      value: planMixData.filter((row) => row.subscriptionStatus?.toLowerCase() === "active").length,
    },
    {
      label: "Inactive/Canceled",
      value: planMixData.filter((row) => ["inactive", "canceled"].includes(row.subscriptionStatus?.toLowerCase())).length,
    },
    { label: "Invoice Total", value: `$${planMixRevenue.toFixed(2)} (${planMixInvoices})`, isText: true },
  ];

  const assetInventoryMetrics = [
    { label: "Total Assets", value: assetInventoryTotal },
    {
      label: "Complete",
      value: assetInventoryData.filter((row) => row.analysisStatus?.toLowerCase() === "complete").length,
    },
    {
      label: "Processing",
      value: assetInventoryData.filter((row) => row.analysisStatus?.toLowerCase() === "processing").length,
    },
    {
      label: "Failed",
      value: assetInventoryData.filter((row) => row.analysisStatus?.toLowerCase() === "failed").length,
    },
  ];

  const completedJobs = analysisPipelineData.filter((row) => row.status?.toLowerCase() === "completed").length;
  const failedJobs = analysisPipelineData.filter((row) => row.status?.toLowerCase() === "failed").length;
  const runningJobs = analysisPipelineData.filter((row) => ["running", "queued"].includes(row.status?.toLowerCase())).length;
  const durationRows = analysisPipelineData.filter((row) => Number.isFinite(Number(row.durationSeconds)));
  const avgDuration = durationRows.length
    ? (durationRows.reduce((sum, row) => sum + Number(row.durationSeconds), 0) / durationRows.length).toFixed(1)
    : "—";

  const analysisPipelineMetrics = [
    { label: "Total Jobs", value: analysisPipelineTotal },
    { label: "Completed", value: completedJobs },
    { label: "Running/Queued", value: runningJobs },
    { label: "Failed • Avg Duration", value: `${failedJobs} • ${avgDuration}s`, isText: true },
  ];

  const totalAssetsByPowerUsers = powerUsersData.reduce((sum, row) => sum + Number(row.assets || 0), 0);
  const totalRevisionsByPowerUsers = powerUsersData.reduce((sum, row) => sum + Number(row.revisions || 0), 0);
  const averageRevisions = powerUsersData.length
    ? (totalRevisionsByPowerUsers / powerUsersData.length).toFixed(1)
    : "0";

  const powerUsersMetrics = [
    { label: "Total Users", value: powerUsersTotal },
    { label: "Assets", value: totalAssetsByPowerUsers },
    { label: "Revisions", value: totalRevisionsByPowerUsers },
    { label: "Avg Revisions/User", value: averageRevisions },
  ];

  return (
    <div className="flex flex-col gap-5" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold text-brand-text">Reports Dashboard</h1>
          <p className="text-sm text-brand-muted">Track report insights and export data from a clean, consolidated view.</p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <DateRangePicker
              startValue={startValue}
              endValue={endValue}
              onStartChange={(v) => { setStartValue(v); setDateRangeError(""); }}
              onEndChange={(v) => { setEndValue(v); setDateRangeError(""); }}
              filterStart={appliedStart}
              filterEnd={appliedEnd}
              onApply={handleApplyDates}
              onClear={handleClearDates}
              error={dateRangeError}
            />
            <Button
              variant="primary"
              className="rounded-lg px-5 bg-[#5B4FCF] hover:bg-[#4F43BE]"
              disabled={(!isUserSignupReport && !isPlanMixReport && !isAssetInventoryReport && !isAnalysisPipelineReport && !isPowerUsersReport && !isPlanVsUsageReport && !isReportGenVolumeReport && !isStaleOrStuckReport && !isEnterpriseFunnelReport && !isActivityAuditReport) || isDownloading}
              onClick={handleDownloadExcel}
              title={
                (!isUserSignupReport && !isPlanMixReport && !isAssetInventoryReport && !isAnalysisPipelineReport && !isPowerUsersReport && !isPlanVsUsageReport && !isReportGenVolumeReport && !isStaleOrStuckReport && !isEnterpriseFunnelReport && !isActivityAuditReport)
                  ? "Excel export is available for configured reports"
                  : "Download Excel"
              }
            >
              <Download className="w-4 h-4" />
              {isDownloading ? "Downloading..." : "Download Excel"}
            </Button>
          </div>
          {dateRangeError && (
            <p className="text-xs text-red-500 font-medium">{dateRangeError}</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 flex flex-col gap-5 border border-surface-border/80">

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-100 text-sm text-red-600 rounded-xl">
            {error}
          </div>
        )}

        {!appliedStart ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="w-14 h-14 rounded-full bg-[#F0EEFF] flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-[#5B4FCF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div>
              <p className="text-base font-semibold text-brand-text">No data to display</p>
              <p className="text-sm text-brand-muted mt-1">
                Please select a <span className="font-medium text-brand-text">Start Date</span> and{" "}
                <span className="font-medium text-brand-text">End Date</span>, then click{" "}
                <span className="font-medium text-[#5B4FCF]">Apply</span> to load the report data.
              </p>
            </div>
          </div>
        ) : isUserSignupReport ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {userSignupMetrics.map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className={`mt-2 ${metric.isText ? "text-sm" : "text-2xl"} font-semibold text-[#5B4FCF]`}>
                    {loading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-white overflow-hidden">
              <div className="overflow-x-auto max-h-[560px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-[#F4F5F8]">
                    <tr className="border-b border-surface-border">
                      {[
                        "S.No",
                        "Name",
                        "Email",
                        "Role",
                        "Plan Tier",
                        "Email Verified",
                        "Company",
                        "Created At",
                      ].map((header) => (
                        <th
                          key={header}
                          className="px-4 py-3 text-left text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted whitespace-nowrap"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="py-16 text-center text-sm text-brand-muted">
                          Loading...
                        </td>
                      </tr>
                    ) : pagedUserSignupRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-14 text-center text-sm text-brand-muted">
                          No rows found.
                        </td>
                      </tr>
                    ) : (
                      pagedUserSignupRows.map((row, index) => (
                        <tr key={row.id ?? index} className="border-b border-surface-border last:border-0 hover:bg-[#F7F8FF] transition-colors">
                          <td className="px-4 py-3.5 text-sm text-brand-muted">
                            {(userSignupPage - 1) * PAGE_SIZE + index + 1}
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#ECE9FF] text-[#5B4FCF] text-xs font-semibold flex items-center justify-center">
                                {getInitials(row.name)}
                              </div>
                              <span className="text-sm font-medium text-brand-text">{row.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-sm text-brand-muted block max-w-[260px] truncate" title={row.email}>
                              {row.email}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getRoleBadgeClass(row.role)}`}>
                              {toTitle(row.role)}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getPlanBadgeClass(row.planTier)}`}>
                              {toTitle(row.planTier)}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${row.emailVerified ? "bg-[#E8F7EE] text-[#177B43]" : "bg-[#FEEBEC] text-[#C43744]"}`}>
                              {row.emailVerified ? "Verified" : "Unverified"}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-sm text-brand-text whitespace-nowrap">{row.company || "—"}</td>
                          <td className="px-4 py-3.5 text-sm text-brand-muted whitespace-nowrap">{formatCleanDate(row.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 border-t border-surface-border flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-brand-muted">
                  {loading
                    ? "Loading..."
                    : `Total: ${totalUsers} • Verified: ${verifiedUsers} • Unverified: ${unverifiedUsers}`}
                </p>
                <Pagination
                  page={userSignupPage}
                  totalPages={userSignupTotalPages}
                  onPageChange={setUserSignupPage}
                />
              </div>
            </div>
          </>
        ) : isPlanMixReport ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {planMixMetrics.map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className={`mt-2 ${metric.isText ? "text-sm" : "text-2xl"} font-semibold text-[#5B4FCF]`}>
                    {planMixLoading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>

            <DataTable
              columns={withSerial(PLAN_MIX_COLUMNS, planMixPage, PLAN_MIX_PAGE_SIZE)}
              rows={planMixData}
              loading={planMixLoading}
              emptyText="No rows found."
              page={planMixPage}
              totalPages={planMixTotalPages}
              onPageChange={setPlanMixPage}
              totalCount={planMixTotal}
              pageSize={PLAN_MIX_PAGE_SIZE}
            />
          </>
        ) : isAssetInventoryReport ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {assetInventoryMetrics.map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className="mt-2 text-2xl font-semibold text-[#5B4FCF]">
                    {assetInventoryLoading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>

            <DataTable
              columns={withSerial(ASSET_INVENTORY_COLUMNS, assetInventoryPage, ASSET_INVENTORY_PAGE_SIZE)}
              rows={assetInventoryData}
              loading={assetInventoryLoading}
              emptyText="No rows found."
              page={assetInventoryPage}
              totalPages={assetInventoryTotalPages}
              onPageChange={setAssetInventoryPage}
              totalCount={assetInventoryTotal}
              pageSize={ASSET_INVENTORY_PAGE_SIZE}
            />
          </>
        ) : isAnalysisPipelineReport ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {analysisPipelineMetrics.map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className={`mt-2 ${metric.isText ? "text-sm" : "text-2xl"} font-semibold text-[#5B4FCF]`}>
                    {analysisPipelineLoading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>

            <DataTable
              columns={withSerial(ANALYSIS_PIPELINE_COLUMNS, analysisPipelinePage, ANALYSIS_PIPELINE_PAGE_SIZE)}
              rows={analysisPipelineData}
              loading={analysisPipelineLoading}
              emptyText="No rows found."
              page={analysisPipelinePage}
              totalPages={analysisPipelineTotalPages}
              onPageChange={setAnalysisPipelinePage}
              totalCount={analysisPipelineTotal}
              pageSize={ANALYSIS_PIPELINE_PAGE_SIZE}
            />
          </>
        ) : isPowerUsersReport ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {powerUsersMetrics.map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className="mt-2 text-2xl font-semibold text-[#5B4FCF]">
                    {powerUsersLoading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>

            <DataTable
              columns={withSerial(POWER_USERS_COLUMNS, powerUsersPage, POWER_USERS_PAGE_SIZE)}
              rows={powerUsersData}
              loading={powerUsersLoading}
              emptyText="No rows found."
              page={powerUsersPage}
              totalPages={powerUsersTotalPages}
              onPageChange={setPowerUsersPage}
              totalCount={powerUsersTotal}
              pageSize={POWER_USERS_PAGE_SIZE}
            />
          </>
        ) : isReportGenVolumeReport ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {[
                { label: "Total Revisions (90d)", value: reportGenVolumeSummary?.totalRevisions90Days ?? "—" },
                { label: "Avg Revisions / Day", value: reportGenVolumeSummary?.averageRevisionsPerDay != null ? Number(reportGenVolumeSummary.averageRevisionsPerDay).toFixed(1) : "—", isText: true },
                { label: "Peak Day Count", value: reportGenVolumeSummary?.highestRevisionDayCount ?? "—" },
                { label: "Top Asset Revisions", value: reportGenVolumeSummary?.topAssetRevisionCount ?? "—" },
              ].map((m) => (
                <div key={m.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{m.label}</p>
                  <p className={`mt-2 ${m.isText ? "text-sm" : "text-2xl"} font-semibold text-[#5B4FCF]`}>
                    {reportGenVolumeLoading ? "..." : m.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Daily revision volume chart — commented out
            <div className="rounded-2xl bg-white border border-surface-border p-4">
              <p className="text-sm font-semibold text-brand-text mb-3">Daily revision volume (last 90 days)</p>
              {reportGenVolumeLoading ? (
                <div className="h-44 flex items-center justify-center text-brand-muted text-sm">Loading...</div>
              ) : (
                <ResponsiveContainer width="100%" height={176}>
                  <BarChart data={reportGenVolumeDailyVolume} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#9CA3AF" }}
                      interval={12}
                      tickFormatter={(d) => { const dt = new Date(d + "T00:00:00"); return `${dt.getMonth() + 1}/${dt.getDate()}`; }}
                    />
                    <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} allowDecimals={false} />
                    <Tooltip
                      formatter={(value) => [value, "Revisions"]}
                      labelFormatter={(label) => `Date: ${label}`}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Bar dataKey="revisionCount" fill="#5B4FCF" radius={[3, 3, 0, 0]} maxBarSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            */}

            <div className="rounded-2xl bg-white overflow-hidden border border-surface-border">
              <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
                <p className="text-sm font-semibold text-brand-text">Top assets by revision count</p>
                <p className="text-xs text-brand-muted">{reportGenVolumeLoading ? "Loading..." : `${reportGenVolumeTopAssets.length} rows`}</p>
              </div>
              <DataTable
                columns={withSerial(REPORT_GEN_VOLUME_COLUMNS)}
                rows={reportGenVolumeTopAssets}
                loading={reportGenVolumeLoading}
                emptyText="No rows found."
              />
            </div>
          </>
        ) : isPlanVsUsageReport ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {[
                { label: "Total Active Users", value: planVsUsageSummary?.totalActiveUsers ?? planVsUsageTotal },
                { label: "Over Asset Limit", value: planVsUsageSummary?.usersOverAssetLimit ?? "—", warn: true },
                { label: "Over Analysis Limit", value: planVsUsageSummary?.usersOverAnalysisLimit ?? "—", warn: true },
                {
                  label: "Avg Asset Usage",
                  value: planVsUsageSummary?.averageAssetUsage != null
                    ? `${Number(planVsUsageSummary.averageAssetUsage).toFixed(1)}%`
                    : "—",
                  isText: true,
                },
              ].map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className={`mt-2 ${
                    metric.isText ? "text-sm" : "text-2xl"
                  } font-semibold ${
                    metric.warn && Number(metric.value) > 0 ? "text-red-500" : "text-[#5B4FCF]"
                  }`}>
                    {planVsUsageLoading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>
            <DataTable
              columns={withSerial(PLAN_VS_USAGE_COLUMNS, planVsUsagePage, PLAN_VS_USAGE_PAGE_SIZE)}
              rows={planVsUsageData}
              loading={planVsUsageLoading}
              emptyText="No rows found."
              page={planVsUsagePage}
              totalPages={planVsUsageTotalPages}
              onPageChange={setPlanVsUsagePage}
              totalCount={planVsUsageTotal}
              pageSize={PLAN_VS_USAGE_PAGE_SIZE}
            />
          </>
        ) : isStaleOrStuckReport ? (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-3">
              {[
                { label: "Total stale assets", value: staleOrStuckSummary?.totalStaleAssets ?? "—" },
                { label: "Oldest stale asset", value: staleOrStuckSummary?.oldestStaleAssetDuration ?? "—", isText: true },
              ].map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className={`mt-2 ${metric.isText ? "text-sm" : "text-2xl"} font-semibold text-[#5B4FCF]`}>
                    {staleOrStuckLoading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>
            {/* Stale assets sub-table */}
            <div className="rounded-2xl bg-white overflow-hidden border border-surface-border">
              <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
                <p className="text-sm font-semibold text-brand-text">Assets pending/processing with no update in 24+ hours</p>
                <p className="text-xs text-brand-muted">{staleOrStuckLoading ? "Loading..." : `${staleAssetsData.length} rows`}</p>
              </div>
              <DataTable
                columns={withSerial(STALE_ASSETS_COLUMNS, staleAssetsPage, STALE_ASSETS_PAGE_SIZE)}
                rows={pagedStaleAssetsRows}
                loading={staleOrStuckLoading}
                emptyText="No rows found."
                page={staleAssetsPage}
                totalPages={staleAssetsTotalPages}
                onPageChange={setStaleAssetsPage}
                totalCount={staleAssetsData.length}
                pageSize={STALE_ASSETS_PAGE_SIZE}
              />
            </div>
          </>
        ) : isActivityAuditReport ? (
          <>
            {/* Summary KPI cards */}
            {activityAuditData.length > 0 || activityAuditTotal > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {[
                  { label: "Total Activity Records", value: activityAuditLoading ? "..." : activityAuditTotal },
                ].map((m) => (
                  <div key={m.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                    <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{m.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-[#5B4FCF]">{m.value}</p>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="rounded-2xl bg-white overflow-hidden border border-surface-border">
              <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
                <p className="text-sm font-semibold text-brand-text">Activity log</p>
                <p className="text-xs text-brand-muted">{activityAuditLoading ? "Loading..." : `${activityAuditTotal} rows`}</p>
              </div>
              <DataTable
                columns={withSerial(ACTIVITY_AUDIT_COLUMNS, activityAuditPage, ACTIVITY_AUDIT_PAGE_SIZE)}
                rows={activityAuditData}
                loading={activityAuditLoading}
                emptyText="No rows found."
                page={activityAuditPage}
                totalPages={activityAuditTotalPages}
                onPageChange={setActivityAuditPage}
                totalCount={activityAuditTotal}
                pageSize={ACTIVITY_AUDIT_PAGE_SIZE}
              />
            </div>
          </>
        ) : isEnterpriseFunnelReport ? (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
              {[
                { label: "Total inquiries", value: enterpriseFunnelSummary?.totalInquiries ?? "—" },
                { label: "Contacted", value: enterpriseFunnelSummary?.contactedInquiries ?? "—" },
                { label: "Pending", value: enterpriseFunnelSummary?.pendingInquiries ?? "—" },
                { label: "Not contacted", value: enterpriseFunnelSummary?.notContactedInquiries ?? "—" },
                {
                  label: "Conversion rate",
                  value: enterpriseFunnelSummary?.conversionRate != null
                    ? `${enterpriseFunnelSummary.conversionRate}%`
                    : "—",
                  isText: true,
                },
              ].map((metric) => (
                <div key={metric.label} className="rounded-xl bg-white border border-surface-border px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-brand-muted">{metric.label}</p>
                  <p className={`mt-2 ${metric.isText ? "text-sm" : "text-2xl"} font-semibold text-[#5B4FCF]`}>
                    {enterpriseFunnelLoading ? "..." : metric.value}
                  </p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl bg-white overflow-hidden border border-surface-border">
              <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
                <p className="text-sm font-semibold text-brand-text">Enterprise inquiries</p>
                <p className="text-xs text-brand-muted">{enterpriseFunnelLoading ? "Loading..." : `${enterpriseFunnelTotal} rows`}</p>
              </div>
              <DataTable
                columns={withSerial(ENTERPRISE_FUNNEL_COLUMNS, enterpriseFunnelPage, ENTERPRISE_FUNNEL_PAGE_SIZE)}
                rows={enterpriseFunnelData}
                loading={enterpriseFunnelLoading}
                emptyText="No rows found."
                page={enterpriseFunnelPage}
                totalPages={enterpriseFunnelTotalPages}
                onPageChange={setEnterpriseFunnelPage}
                totalCount={enterpriseFunnelTotal}
                pageSize={ENTERPRISE_FUNNEL_PAGE_SIZE}
              />
            </div>
          </>
        ) : (
          <div className="border border-surface-border rounded-xl p-10 text-center">
            <h2 className="text-lg font-semibold text-brand-text">{currentReport.label}</h2>
            <p className="text-sm text-brand-muted mt-2">
              Listing API is not connected for this report yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReportsPage;
